import type { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { Geofence } from "../../../../../../store/admin/geofencesStore";
import type { ThemeClasses, PointCoordinate } from "../types";
import Modal from "../../../../../../components/base/Modal";
import { APIProvider } from "@vis.gl/react-google-maps";
import { GeofenceForm } from "./GeofenceForm";
import { GeofenceMapPreview } from "./GeofenceMapPreview";
import { useDisableCRUD } from "../../../../../../store/admin/companiesStore";

interface GeofenceModalProps {
  isFromTracking?: boolean;
  isLinearOnly?: boolean;
  isGeofencesOnly?: boolean;
  hideNotificationFields?: boolean;
  isOpen: boolean;
  isEdit: boolean;
  loadingSubmit: boolean;
  form: UseFormReturn<Geofence>;
  shape: string;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  editGeofence: Geofence | null;
  polygonPoints: PointCoordinate[];
  setPolygonPoints: (points: PointCoordinate[]) => void;
  linearPoints: PointCoordinate[];
  setLinearPoints: (points: PointCoordinate[]) => void;
  routeDistance: number;
  estimatedTime: string;
  polygonArea: number;
  defaultCircularCoordinates: {
    latitude: number;
    longitude: number;
    radius: number;
  };
  hasRealChanges: () => boolean;
  onAddressSelect: (address: string, lat: number, lng: number) => void;
  onLinearPointsChange: (newPoints: { lat: number; lng: number }[]) => void;
  onAxisCalculated: (path: PointCoordinate[]) => void;
  onBufferChange: (value: number) => void;
  linearAddresses: { address: string | null; geocoding: boolean }[];
  onLinearAddressEdit: (index: number, address: string) => void;
  onRouteCalculated: (distance: number) => void;
  onSubmit: (data: any) => void;
  onClose: () => void;
}

export function GeofenceModal({
  isFromTracking,
  isLinearOnly = false,
  isGeofencesOnly = false,
  hideNotificationFields = false,
  isOpen,
  isEdit,
  loadingSubmit,
  form,
  shape,
  themeClasses,
  isDarkMode,
  editGeofence,
  polygonPoints,
  setPolygonPoints,
  linearPoints,
  setLinearPoints,
  routeDistance,
  estimatedTime,
  polygonArea,
  defaultCircularCoordinates,
  hasRealChanges,
  onAddressSelect,
  onLinearPointsChange,
  onAxisCalculated,
  onBufferChange,
  linearAddresses,
  onLinearAddressEdit,
  onRouteCalculated,
  onSubmit,
  onClose,
}: GeofenceModalProps) {
  const { t } = useTranslation("adminSections");
  const disableCRUD = useDisableCRUD();

  if (!isOpen) return null;

  return (
    <Modal
      title={
        isEdit
          ? isLinearOnly
            ? t("geofences.modal.editRouteTitle", "Editar Ruta Lineal")
            : t("geofences.modal.editTitle", "Editar Geocerca")
          : isLinearOnly
            ? t("geofences.modal.createRouteTitle", "Nueva Ruta Lineal")
            : t("geofences.modal.createTitle", "Nueva Geocerca")
      }
      onClose={onClose}
      themeClasses={themeClasses}
      width="w-full max-w-2xl"
      disableCRUD={disableCRUD}
      content={
        <APIProvider
          apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
          libraries={["places"]}
        >
          <GeofenceForm
            isFromTracking={isFromTracking}
            isLinearOnly={isLinearOnly}
            isGeofencesOnly={isGeofencesOnly}
            hideNotificationFields={hideNotificationFields}
            form={form}
            shape={shape}
            isEdit={isEdit}
            loadingSubmit={loadingSubmit}
            hasRealChanges={hasRealChanges}
            themeClasses={themeClasses}
            isDarkMode={isDarkMode}
            editGeofence={editGeofence}
            polygonPoints={polygonPoints}
            setPolygonPoints={setPolygonPoints}
            linearPoints={linearPoints}
            setLinearPoints={setLinearPoints}
            defaultCircularCoordinates={defaultCircularCoordinates}
            onAddressSelect={onAddressSelect}
            onBufferChange={onBufferChange}
            linearAddresses={linearAddresses}
            onLinearAddressEdit={onLinearAddressEdit}
            onSubmit={onSubmit}
            onCancel={onClose}
            mapSlot={
              <div className="w-full h-full rounded-xl overflow-hidden">
                <GeofenceMapPreview
                  shape={shape}
                  form={{
                    watch: form.watch,
                    setValue: form.setValue,
                    trigger: form.trigger,
                  }}
                  themeClasses={themeClasses}
                  isDarkMode={isDarkMode}
                  isEdit={isEdit}
                  polygonPoints={polygonPoints}
                  setPolygonPoints={setPolygonPoints}
                  linearPoints={linearPoints}
                  defaultCircularCoordinates={defaultCircularCoordinates}
                  onLinearPointsChange={onLinearPointsChange}
                  onAxisCalculated={onAxisCalculated}
                  corridorBuffer={Number(form.watch("buffer")) || 0}
                  onRouteCalculated={onRouteCalculated}
                  routeDistance={routeDistance}
                  estimatedTime={estimatedTime}
                  polygonArea={polygonArea}
                />
              </div>
            }
          />
        </APIProvider>
      }
    />
  );
}
